import { useState, useId, useMemo } from 'react';
import {
  UserPlus,
  Loader2,
  Pencil,
  UserX,
  UserCheck,
  Users,
  ShieldCheck,
  Shield,
  Building2,
  KeyRound,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { UserRole } from '@pms/shared';
import type { User } from '@pms/shared';
import { cn } from '@/lib/utils';
import { userDisplayName, userIdentifier } from '@/lib/user-display';
import { useAuth } from '@/hooks/useAuth';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { StatCard } from '@/components/ui/StatCard';
import { EmptyState } from '@/components/ui/EmptyState';
import { SlideOver } from '@/components/ui/SlideOver';
import {
  useUsers,
  useInviteUser,
  useUpdateUser,
  useDeactivateUser,
  useResetPassword,
  useUserProperties,
  useAssignUserProperties,
} from '@/hooks/api/useUsers';
import { usePropertyContext } from '@/contexts/PropertyContext';
import { CredentialsBlock } from '@/components/CredentialsBlock';
import { extractErrorMessage } from '@/lib/error';
import type {
  InviteUserPayload,
  InviteUserResponse,
  UpdateUserPayload,
} from '@/services/api/users.api';

// ─── Constants ───────────────────────────────────────────────────────────────

const ROLE_LABELS: Record<UserRole, string> = {
  // super_admin is never surfaced in the tenant-facing UI; neutral fallback only.
  [UserRole.SUPER_ADMIN]: 'Admin',
  [UserRole.ADMIN]: 'Admin',
  [UserRole.REVENUE_MANAGER]: 'Revenue Manager',
  [UserRole.OPERATIONS]: 'Operations',
  [UserRole.OWNER]: 'Owner',
};

const ROLE_STYLES: Record<UserRole, string> = {
  [UserRole.SUPER_ADMIN]: 'bg-gray-100 text-gray-700',
  [UserRole.ADMIN]: 'bg-purple-100 text-purple-700',
  [UserRole.REVENUE_MANAGER]: 'bg-blue-100 text-blue-700',
  [UserRole.OPERATIONS]: 'bg-green-100 text-green-700',
  [UserRole.OWNER]: 'bg-amber-100 text-amber-700',
};

/** Roles that can be assigned from the UI (excludes super_admin). */
const ASSIGNABLE_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.REVENUE_MANAGER,
  UserRole.OPERATIONS,
  UserRole.OWNER,
];

function fmtDate(iso: string): string {
  try {
    return format(parseISO(iso), 'MMM d, yyyy');
  } catch {
    return iso;
  }
}

/**
 * User label: full name → username → email → '—'. Shared helper so a
 * username-only staff member (Hotel Groups, null email) never renders blank.
 */
function displayName(user: User): string {
  return userDisplayName(user);
}

// ─── Property Checkboxes ────────────────────────────────────────────────────

interface PropertyCheckboxesProps {
  properties: Array<{ id: string; name: string }>;
  selectedIds: Set<string>;
  onChange: (ids: Set<string>) => void;
  label?: string;
}

function PropertyCheckboxes({ properties, selectedIds, onChange, label = 'Properties' }: PropertyCheckboxesProps) {
  const legendId = useId();

  const handleToggle = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    onChange(next);
  };

  return (
    <fieldset>
      <legend id={legendId} className="block text-sm font-medium text-gray-700 mb-2">
        {label}
      </legend>
      {properties.length === 0 ? (
        <p className="text-sm text-gray-400">No properties available.</p>
      ) : (
        <div className="space-y-2 max-h-48 overflow-y-auto rounded-lg border border-gray-200 p-3">
          {properties.map((prop) => (
            <label
              key={prop.id}
              className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 rounded px-1 py-0.5"
            >
              <input
                type="checkbox"
                checked={selectedIds.has(prop.id)}
                onChange={() => handleToggle(prop.id)}
                className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">{prop.name}</span>
            </label>
          ))}
        </div>
      )}
    </fieldset>
  );
}

// ─── User Property Badges ───────────────────────────────────────────────────

function UserPropertyBadges({ userId }: { userId: string }) {
  const { data: userProps, isLoading } = useUserProperties(userId);

  if (isLoading) {
    return <Loader2 size={12} className="animate-spin text-gray-400" aria-label="Loading properties" />;
  }

  if (!userProps || userProps.length === 0) {
    return <span className="text-xs text-gray-400">No properties</span>;
  }

  return (
    <div className="flex flex-wrap gap-1">
      {userProps.map((p) => (
        <span
          key={p.id}
          className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700"
        >
          <Building2 size={10} aria-hidden="true" />
          {p.name}
        </span>
      ))}
    </div>
  );
}

// ─── Invite User Form ────────────────────────────────────────────────────────

interface InviteFormProps {
  onClose: () => void;
}

function InviteUserForm({ onClose }: InviteFormProps) {
  const emailId = useId();
  const usernameId = useId();
  const usernameHelpId = useId();
  const firstNameId = useId();
  const lastNameId = useId();
  const roleId = useId();

  const { properties, selectedProperty } = usePropertyContext();

  const [identityMode, setIdentityMode] = useState<'email' | 'username'>('email');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState<UserRole>(UserRole.OPERATIONS);
  const [selectedPropertyIds, setSelectedPropertyIds] = useState<Set<string>>(
    () => new Set(selectedProperty ? [selectedProperty.id] : []),
  );
  const [invited, setInvited] = useState<InviteUserResponse | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const inviteMutation = useInviteUser();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (identityMode === 'email' && !email.trim()) {
      setFormError('Enter an email address.');
      return;
    }
    if (identityMode === 'username' && !username.trim()) {
      setFormError('Enter a username.');
      return;
    }

    const identity: Pick<InviteUserPayload, 'email' | 'username'> =
      identityMode === 'email'
        ? { email: email.trim() }
        : { username: username.trim() };

    const payload: InviteUserPayload = {
      ...identity,
      role,
      ...(firstName.trim() ? { firstName: firstName.trim() } : {}),
      ...(lastName.trim() ? { lastName: lastName.trim() } : {}),
      ...(selectedPropertyIds.size > 0
        ? { propertyIds: Array.from(selectedPropertyIds) }
        : {}),
    };
    inviteMutation.mutate(payload, {
      onSuccess: (data) => {
        setInvited(data);
      },
      onError: (err) => setFormError(extractErrorMessage(err)),
    });
  };

  if (invited) {
    const usernameOnly = !invited.user.email && !!invited.user.username;
    return (
      <div className="space-y-4">
        <div
          className="rounded-lg border border-green-200 bg-green-50 p-4"
          role="status"
          aria-live="polite"
        >
          <p className="text-sm font-medium text-green-800">
            User invited successfully
          </p>
          <p className="mt-1 text-sm text-green-700">
            Share these credentials with the new user — Copy pastes all three:
          </p>
          <div className="mt-2">
            <CredentialsBlock
              group={invited.groupCode}
              username={invited.user.username}
              password={invited.temporaryPassword}
            />
          </div>
          {!usernameOnly && (
            <p className="mt-3 text-xs text-green-700">
              An invite email was also sent to {invited.user.email}.
            </p>
          )}
          <p className="mt-1 text-xs text-green-700">
            The password is shown once and can&apos;t be retrieved later.
          </p>
        </div>
        <Button variant="secondary" fullWidth onClick={onClose}>
          Done
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
      <fieldset>
        <legend className="block text-sm font-medium text-gray-700 mb-1">
          Identity <span aria-label="required" className="text-red-500">*</span>
        </legend>
        <div className="flex flex-wrap gap-4" role="radiogroup" aria-label="Identity">
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input
              type="radio"
              name="invite-identity"
              value="email"
              checked={identityMode === 'email'}
              onChange={() => setIdentityMode('email')}
              className="h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            Email
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input
              type="radio"
              name="invite-identity"
              value="username"
              checked={identityMode === 'username'}
              onChange={() => setIdentityMode('username')}
              className="h-4 w-4 border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            Username-only (no email)
          </label>
        </div>
      </fieldset>

      {identityMode === 'email' ? (
        <div>
          <label htmlFor={emailId} className="block text-sm font-medium text-gray-700 mb-1">
            Email <span aria-label="required" className="text-red-500">*</span>
          </label>
          <input
            id={emailId}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="user@example.com"
          />
        </div>
      ) : (
        <div>
          <label htmlFor={usernameId} className="block text-sm font-medium text-gray-700 mb-1">
            Username <span aria-label="required" className="text-red-500">*</span>
          </label>
          <input
            id={usernameId}
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            maxLength={64}
            autoCapitalize="none"
            aria-describedby={usernameHelpId}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            placeholder="reception"
          />
          <p id={usernameHelpId} className="mt-1 text-xs text-gray-500">
            Unique within your group. Login = group code + username. No invite
            email is sent.
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor={firstNameId} className="block text-sm font-medium text-gray-700 mb-1">
            First Name
          </label>
          <input
            id={firstNameId}
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            maxLength={100}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div>
          <label htmlFor={lastNameId} className="block text-sm font-medium text-gray-700 mb-1">
            Last Name
          </label>
          <input
            id={lastNameId}
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            maxLength={100}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>

      <div>
        <label htmlFor={roleId} className="block text-sm font-medium text-gray-700 mb-1">
          Role <span aria-label="required" className="text-red-500">*</span>
        </label>
        <select
          id={roleId}
          value={role}
          onChange={(e) => setRole(e.target.value as UserRole)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
        >
          {ASSIGNABLE_ROLES.map((r) => (
            <option key={r} value={r}>
              {ROLE_LABELS[r]}
            </option>
          ))}
        </select>
      </div>

      <PropertyCheckboxes
        properties={properties}
        selectedIds={selectedPropertyIds}
        onChange={setSelectedPropertyIds}
        label="Property Access"
      />

      {formError && (
        <div
          className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
          role="alert"
        >
          <span>{formError}</span>
        </div>
      )}

      <Button type="submit" fullWidth isLoading={inviteMutation.isPending}>
        <UserPlus size={16} aria-hidden="true" />
        Send Invite
      </Button>
    </form>
  );
}

// ─── Edit User Form ──────────────────────────────────────────────────────────

interface EditFormProps {
  user: User;
  currentUserId: string;
  onClose: () => void;
}

function EditUserForm({ user, currentUserId, onClose }: EditFormProps) {
  const firstNameId = useId();
  const lastNameId = useId();
  const roleId = useId();
  const activeId = useId();

  const { properties } = usePropertyContext();
  const { data: userProps } = useUserProperties(user.id);

  const [firstName, setFirstName] = useState(user.firstName ?? '');
  const [lastName, setLastName] = useState(user.lastName ?? '');
  const [role, setRole] = useState<UserRole>(user.role);
  const [isActive, setIsActive] = useState(user.isActive);
  const [selectedPropertyIds, setSelectedPropertyIds] = useState<Set<string>>(
    () => new Set(userProps?.map((p) => p.id) ?? []),
  );
  const [propsInitialized, setPropsInitialized] = useState(false);

  // Sync property selection when data loads
  if (userProps && !propsInitialized) {
    const ids = new Set(userProps.map((p) => p.id));
    setSelectedPropertyIds(ids);
    setPropsInitialized(true);
  }

  const updateMutation = useUpdateUser();
  const assignPropsMutation = useAssignUserProperties();

  const isSelf = user.id === currentUserId;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload: UpdateUserPayload = {};

    if (firstName.trim() !== (user.firstName ?? '')) payload.firstName = firstName.trim();
    if (lastName.trim() !== (user.lastName ?? '')) payload.lastName = lastName.trim();
    if (!isSelf && role !== user.role) payload.role = role;
    if (isActive !== user.isActive) payload.isActive = isActive;

    // Save property assignments in parallel
    const propertyIds = Array.from(selectedPropertyIds);
    assignPropsMutation.mutate({ userId: user.id, propertyIds });

    updateMutation.mutate(
      { id: user.id, payload },
      { onSuccess: () => onClose() },
    );
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <span className="block text-sm font-medium text-gray-700 mb-1">
          {user.email ? 'Email' : 'Username'}
        </span>
        <p className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500">
          {userIdentifier(user)}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor={firstNameId} className="block text-sm font-medium text-gray-700 mb-1">
            First Name
          </label>
          <input
            id={firstNameId}
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            maxLength={100}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div>
          <label htmlFor={lastNameId} className="block text-sm font-medium text-gray-700 mb-1">
            Last Name
          </label>
          <input
            id={lastNameId}
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            maxLength={100}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>

      <div>
        <label htmlFor={roleId} className="block text-sm font-medium text-gray-700 mb-1">
          Role
        </label>
        <select
          id={roleId}
          value={role}
          onChange={(e) => setRole(e.target.value as UserRole)}
          disabled={isSelf}
          className={cn(
            'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white',
            isSelf && 'opacity-50 cursor-not-allowed',
          )}
        >
          {ASSIGNABLE_ROLES.map((r) => (
            <option key={r} value={r}>
              {ROLE_LABELS[r]}
            </option>
          ))}
        </select>
        {isSelf && (
          <p className="mt-1 text-xs text-gray-400">You cannot change your own role.</p>
        )}
      </div>

      <PropertyCheckboxes
        properties={properties}
        selectedIds={selectedPropertyIds}
        onChange={setSelectedPropertyIds}
        label="Property Access"
      />

      <div className="flex items-center gap-3">
        <label htmlFor={activeId} className="relative inline-flex cursor-pointer items-center">
          <input
            id={activeId}
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            className="peer sr-only"
          />
          <div className="h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary-600 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus-visible:ring-2 peer-focus-visible:ring-primary-500 peer-focus-visible:ring-offset-2" />
        </label>
        <span className="text-sm font-medium text-gray-700">
          {isActive ? 'Active' : 'Inactive'}
        </span>
      </div>

      {(updateMutation.isError || assignPropsMutation.isError) && (
        <p className="text-sm text-red-600" role="alert">
          Failed to update user. Please try again.
        </p>
      )}

      <Button
        type="submit"
        fullWidth
        isLoading={updateMutation.isPending || assignPropsMutation.isPending}
      >
        Update
      </Button>
    </form>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function UserManagementPage() {
  const { user: currentUser } = useAuth();
  const { data: rawUsers, isLoading, isError } = useUsers();
  // Defense-in-depth: super_admin users must never appear in a tenant's user list.
  const users = useMemo(
    () => rawUsers?.filter((u) => u.role !== UserRole.SUPER_ADMIN),
    [rawUsers],
  );

  const [showInvite, setShowInvite] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);

  const deactivateMutation = useDeactivateUser();
  const reactivateMutation = useUpdateUser();
  const resetMutation = useResetPassword();
  const [resetResult, setResetResult] = useState<{
    name: string;
    password: string;
  } | null>(null);

  // Access control: only admin / super_admin
  if (
    currentUser &&
    currentUser.role !== UserRole.ADMIN &&
    currentUser.role !== UserRole.SUPER_ADMIN
  ) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <ShieldCheck size={48} className="text-gray-300 mb-4" aria-hidden="true" />
        <h2 className="text-lg font-semibold text-gray-700">Access Denied</h2>
        <p className="mt-1 text-sm text-gray-400">
          You do not have permission to view this page.
        </p>
      </div>
    );
  }

  // Stats
  const activeCount = users?.filter((u) => u.isActive).length ?? 0;
  const roleCounts = useMemo(() => {
    if (!users) return {};
    const counts: Partial<Record<UserRole, number>> = {};
    for (const u of users) {
      counts[u.role] = (counts[u.role] ?? 0) + 1;
    }
    return counts;
  }, [users]);

  const handleDeactivate = (user: User) => {
    const action = user.isActive ? 'deactivate' : 'reactivate';
    const confirmed = window.confirm(
      `Are you sure you want to ${action} ${displayName(user)}?`,
    );
    if (!confirmed) return;

    if (user.isActive) {
      deactivateMutation.mutate(user.id);
    } else {
      reactivateMutation.mutate({ id: user.id, payload: { isActive: true } });
    }
  };

  const handleReset = (user: User) => {
    const confirmed = window.confirm(
      `Re-issue a one-time temporary password for ${displayName(user)}? Their ` +
        `current password stops working and they must set a new one at next login.`,
    );
    if (!confirmed) return;
    resetMutation.mutate(user.id, {
      onSuccess: (data) =>
        setResetResult({
          name: displayName(user),
          password: data.temporaryPassword,
        }),
    });
  };

  return (
    <div>
      <PageHeader
        title="Team Management"
        description="Invite and manage team members"
        actions={
          <Button onClick={() => setShowInvite(true)}>
            <UserPlus size={16} aria-hidden="true" />
            Invite User
          </Button>
        }
      />

      {/* Stats Row */}
      {!isLoading && users && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard
            icon={Users}
            value={activeCount}
            label="Active Users"
            borderColor="border-l-primary-500"
          />
          <StatCard
            icon={Shield}
            value={users.length}
            label="Total Users"
            borderColor="border-l-gray-400"
          />
          <div className="col-span-2 flex items-center gap-2 flex-wrap bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <span className="text-sm font-medium text-gray-500 mr-2">By Role:</span>
            {Object.entries(roleCounts).map(([role, count]) => (
              <span
                key={role}
                className={cn(
                  'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium',
                  ROLE_STYLES[role as UserRole] ?? 'bg-gray-100 text-gray-700',
                )}
              >
                {ROLE_LABELS[role as UserRole] ?? role}: {count}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} className="animate-spin text-gray-400" aria-label="Loading users" />
        </div>
      )}

      {/* Error */}
      {isError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700" role="alert">
          Failed to load users. Please refresh and try again.
        </div>
      )}

      {/* Empty */}
      {!isLoading && !isError && users && users.length === 0 && (
        <EmptyState
          icon={Users}
          title="No team members"
          description="Invite your first team member to get started."
          actionLabel="Invite User"
          onAction={() => setShowInvite(true)}
        />
      )}

      {/* Users Table */}
      {!isLoading && !isError && users && users.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Identifier
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Properties
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Joined
                  </th>
                  <th scope="col" className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {users.map((u) => {
                  const isSelf = u.id === currentUser?.id;
                  return (
                    <tr key={u.id} className={cn(!u.isActive && 'opacity-60')}>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {displayName(u)}
                          {isSelf && (
                            <span className="ml-2 text-xs text-gray-400">(you)</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {userIdentifier(u)}
                        {!u.email && u.username && (
                          <span className="ml-1.5 text-xs text-gray-400">(username)</span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span
                          className={cn(
                            'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
                            ROLE_STYLES[u.role] ?? 'bg-gray-100 text-gray-700',
                          )}
                        >
                          {ROLE_LABELS[u.role] ?? u.role}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <UserPropertyBadges userId={u.id} />
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span
                          className={cn(
                            'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
                            u.isActive
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-500',
                          )}
                        >
                          {u.isActive ? 'Active' : 'Inactive'}
                        </span>
                        {u.mustChangePassword && (
                          <span
                            className="ml-1.5 inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-700"
                            title="Has a one-time temp password; must set a new one at first login"
                          >
                            Pending first login
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {fmtDate(u.createdAt)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            onClick={() => setEditingUser(u)}
                            className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                            aria-label={`Edit ${displayName(u)}`}
                          >
                            <Pencil size={14} aria-hidden="true" />
                            Edit
                          </button>
                          {!isSelf && (
                            <button
                              type="button"
                              onClick={() => handleReset(u)}
                              className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                              aria-label={`Reset password for ${displayName(u)}`}
                            >
                              <KeyRound size={14} aria-hidden="true" />
                              Reset
                            </button>
                          )}
                          {!isSelf && (
                            <button
                              type="button"
                              onClick={() => handleDeactivate(u)}
                              className={cn(
                                'inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                                u.isActive
                                  ? 'text-red-600 hover:bg-red-50'
                                  : 'text-green-600 hover:bg-green-50',
                              )}
                              aria-label={
                                u.isActive
                                  ? `Deactivate ${displayName(u)}`
                                  : `Reactivate ${displayName(u)}`
                              }
                            >
                              {u.isActive ? (
                                <>
                                  <UserX size={14} aria-hidden="true" />
                                  Deactivate
                                </>
                              ) : (
                                <>
                                  <UserCheck size={14} aria-hidden="true" />
                                  Reactivate
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Invite SlideOver */}
      <SlideOver
        isOpen={showInvite}
        onClose={() => setShowInvite(false)}
        title="Invite User"
      >
        <InviteUserForm onClose={() => setShowInvite(false)} />
      </SlideOver>

      {/* Edit SlideOver */}
      <SlideOver
        isOpen={editingUser !== null}
        onClose={() => setEditingUser(null)}
        title="Edit User"
      >
        {editingUser && currentUser && (
          <EditUserForm
            user={editingUser}
            currentUserId={currentUser.id}
            onClose={() => setEditingUser(null)}
          />
        )}
      </SlideOver>

      {/* One-time temp password after a reset — shown once */}
      {resetResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <div className="flex items-center gap-2 mb-2">
              <KeyRound size={18} className="text-amber-600" aria-hidden="true" />
              <h3 className="text-base font-semibold text-gray-900">
                Temporary password for {resetResult.name}
              </h3>
            </div>
            <p className="text-sm text-gray-600 mb-3">
              Share this securely. It is shown <strong>once</strong>; the user must
              set a new password at next login.
            </p>
            <div className="flex items-center gap-2 mb-4">
              <code className="flex-1 rounded-lg bg-gray-100 px-3 py-2 text-sm font-mono text-gray-900 break-all">
                {resetResult.password}
              </code>
              <button
                type="button"
                onClick={() =>
                  void navigator.clipboard?.writeText(resetResult.password)
                }
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Copy
              </button>
            </div>
            <div className="flex justify-end">
              <Button onClick={() => setResetResult(null)}>Done</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
